/**
 * Long-press decision logic for the canvas object context menu.
 *
 * A long press is a near-stationary press held for LONG_PRESS_MS.
 * Moving further than LONG_PRESS_TOLERANCE_PX from the press point
 * makes it a drag and cancels the press - the menu must never fire
 * mid-drag. This module is the pure decision core (the DOM/timer
 * wiring lives in CheckBuilder's attachTouchSelection), so the
 * timing/cancellation rules are unit-testable without a canvas.
 */

export const LONG_PRESS_MS = 500;
export const LONG_PRESS_TOLERANCE_PX = 8;

export class LongPressTracker {
    private startX = 0;
    private startY = 0;
    private startAt = 0;
    private active = false;
    private cancelled = false;

    /** Begin tracking a press at (x, y) at time `now` (ms). */
    begin(x: number, y: number, now: number): void {
        this.startX = x;
        this.startY = y;
        this.startAt = now;
        this.active = true;
        this.cancelled = false;
    }

    /**
     * Report movement; returns true when this move cancels the press
     * (the pointer strayed beyond the tolerance - it is a drag now).
     */
    move(x: number, y: number): boolean {
        if (!this.active || this.cancelled) return this.cancelled;
        if (Math.hypot(x - this.startX, y - this.startY) > LONG_PRESS_TOLERANCE_PX) {
            this.cancelled = true;
        }
        return this.cancelled;
    }

    /** Has the press been held long enough, without cancelling? */
    isDue(now: number): boolean {
        return this.active && !this.cancelled && now - this.startAt >= LONG_PRESS_MS;
    }

    /** When the press becomes due (ms timestamp), or null if not tracking. */
    dueAt(): number | null {
        return this.active ? this.startAt + LONG_PRESS_MS : null;
    }

    get isCancelled(): boolean {
        return this.cancelled;
    }

    get isActive(): boolean {
        return this.active && !this.cancelled;
    }

    /** The press ended (pointer up/cancel) or was consumed. */
    reset(): void {
        this.active = false;
        this.cancelled = false;
    }
}
